/**
 * Resolución del dominio público (feed) de un tenant.
 *
 * La tabla de dominios no es confiable para esto: la bandera `main` está
 * seteada de forma inconsistente (a veces en el dominio raíz, a veces en el
 * `feed.`, a veces en los dos) y hay registros con protocolo o `www.`.
 * Por eso, en vez de asumir "feed." + main, se busca el dominio de feed real
 * entre todos los dominios del tenant.
 */

const cleanDomain = domain => {
  if (!domain || typeof domain !== 'string') return '';
  return domain
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '');
};

const isFeed = domain => /^feed\./i.test(domain);
const isActive = domain => Number(domain?.status) === 1;
const isMain = domain => Number(domain?.main) === 1;

/**
 * Lista de dominios del tenant (objetos del store), sin duplicados y con el
 * campo `domain` normalizado.
 */
const getTenantDomains = (tenant, domainsById) => {
  const ids = [
    ...(tenant?.main_domain || []),
    ...(tenant?.domains || [])
  ].filter(id => id !== null && id !== undefined);

  const seen = [];
  const domains = [];
  ids.forEach(id => {
    if (seen.indexOf(id) !== -1) return;
    seen.push(id);
    const domain = domainsById?.[id];
    const cleaned = cleanDomain(domain?.domain);
    if (cleaned) domains.push({ ...domain, domain: cleaned });
  });

  return domains;
};

/**
 * Devuelve el host del sitio público (feed) del tenant, o null si no se puede
 * determinar con certeza.
 *
 * Solo se considera un dominio `feed.*` activo (status = 1): derivar el feed
 * concatenando "feed." al dominio principal genera links rotos para los
 * tenants que todavía no publicaron su web. Ante varios candidatos se prioriza
 * el marcado como `main` y, si sigue habiendo empate, el más reciente (id más
 * alto), que es el que suele estar vigente.
 */
export const getFeedDomain = (tenant, domainsById) => {
  const feeds = getTenantDomains(tenant, domainsById)
    .filter(d => isFeed(d.domain) && isActive(d))
    .sort((a, b) => Number(b.id || 0) - Number(a.id || 0));

  const feed = feeds.find(d => isMain(d)) || feeds[0];

  return feed ? feed.domain : null;
};

/**
 * URL pública de una propiedad en el sitio del tenant.
 */
export const getPropertyPublicUrl = (tenant, domainsById, idProperty) => {
  if (!idProperty) return null;
  const feedDomain = getFeedDomain(tenant, domainsById);
  if (!feedDomain) return null;

  return `https://${feedDomain}/property/${idProperty}`;
};

export default { getFeedDomain, getPropertyPublicUrl };
